import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateChannelsList } from '@/entities/channel';
import { useInvalidateChannelsOfCompanyList } from '@/entities/company';
import { deleteChannelFromCompany as deleteChannelFromCompanyApi } from '@/shared/api/companies';
import { useGetConfirmation } from '@/shared/ui/Confirmations/confirmation';
import { useNotifications } from '@/shared/ui/Notifications';

export function useDeleteChannelFromCompany() {
  const { t } = useTranslation();

  const { addNotification } = useNotifications();

  const invalidateChannelsOfCompanyList = useInvalidateChannelsOfCompanyList();
  const invalidateChannelsList = useInvalidateChannelsList();
  const { getConfirmation, setPending, closeConfirmation } =
    useGetConfirmation();

  const deleteChannelFromMutation = useMutation({
    mutationFn: ({
      companyId,
      channelId,
    }: {
      companyId: string;
      channelId: string;
    }): Promise<void> => deleteChannelFromCompanyApi(companyId, channelId),
    onSuccess: async (_, { companyId }) => {
      await Promise.all([
        invalidateChannelsOfCompanyList(companyId),
        invalidateChannelsList(),
      ]);
    },
    onSettled: () => {
      setPending(false);
      closeConfirmation();
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  const deleteChannelFromCompany = async (
    companyId: string,
    channelId: string
  ): Promise<void> => {
    const isConfirm = await getConfirmation({
      shouldCloseOnConfirm: false,
      title: t('company:channelsList.delete.title'),
      description: t('company:channelsList.delete.description'),
      confirmText: t('company:channelsList.delete.confirmText'),
    });

    if (!isConfirm) return;

    setPending(true);
    return deleteChannelFromMutation.mutateAsync({ companyId, channelId });
  };

  return {
    deleteChannelFromCompany,
    ...deleteChannelFromMutation,
  };
}
